/** Parse markdown bullet lines from an insight section. */
export const stripMd = (text) =>
  String(text || "")
    .replace(/\*\*/g, "")
    .replace(/^[-*]\s+/gm, "")
    .trim();

const isBulletLine = (line) =>
  line.startsWith("-") || line.startsWith("*") || /^\d+\./.test(line);

const normalizeBulletLine = (line) =>
  stripMd(line.replace(/^[-*]\s+/, "").replace(/^\d+\.\s+/, ""));

export const parseInsightBullets = (section) => {
  const lines = String(section || "")
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);

  const bullets = [];
  for (const line of lines) {
    if (isBulletLine(line)) {
      bullets.push(normalizeBulletLine(line));
      continue;
    }
    // Continuation lines (common in LLM markdown) append to the previous bullet.
    if (bullets.length) {
      bullets[bullets.length - 1] = `${bullets[bullets.length - 1]} ${stripMd(line)}`.trim();
    }
  }

  return bullets.filter(Boolean);
};

/** Split a bullet into display title and body (first colon separates label from detail). */
export const splitBulletTitleBody = (text) => {
  const cleaned = stripMd(text);
  if (!cleaned) return { title: "", body: "", titleOnly: false };

  const colonIdx = cleaned.indexOf(":");
  if (colonIdx > 0) {
    const title = cleaned.slice(0, colonIdx).trim();
    const body = cleaned.slice(colonIdx + 1).trim();
    if (body) return { title, body, titleOnly: false };
    return { title, body: "", titleOnly: true };
  }

  return { title: "", body: cleaned, titleOnly: false };
};

/**
 * Parse markdown bullets into { title, body, hideBody? } rows.
 * Merges title-only bullets (e.g. "Data Validation:") with the following bullet body when present.
 */
export const parseTitledBullets = (section, labelPrefix = "Item") => {
  const bullets = parseInsightBullets(section);
  if (!bullets.length) return [];

  const items = [];
  for (let i = 0; i < bullets.length; i++) {
    const parsed = splitBulletTitleBody(bullets[i]);

    if (parsed.titleOnly) {
      const nextRaw = bullets[i + 1];
      if (nextRaw) {
        const next = splitBulletTitleBody(nextRaw);
        const body = next.body || (!next.titleOnly && next.title ? next.title : "");
        if (body) {
          items.push({ title: parsed.title, body });
          i += 1;
          continue;
        }
      }
      items.push({ title: parsed.title, body: "", hideBody: true });
      continue;
    }

    if (!parsed.title && parsed.body) {
      const wordCount = parsed.body.split(/\s+/).filter(Boolean).length;
      if (wordCount <= 14) {
        items.push({ title: parsed.body, body: "", hideBody: true });
      } else {
        items.push({
          title: `${labelPrefix} ${items.length + 1}`,
          body: parsed.body,
        });
      }
      continue;
    }

    items.push({
      title: parsed.title,
      body: parsed.body && parsed.body !== parsed.title ? parsed.body : parsed.title,
      hideBody: parsed.body === parsed.title,
    });
  }

  return items;
};
